import React, { useEffect, useState } from 'react';
import { X, Link2, Loader2, Check, AlertTriangle, RefreshCw, Package, Copy, Truck, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALLMX_CATEGORIES = [
  'Abbigliamento', 'Adesivi', 'Filtri aria', 'Freni', 'Leve', 'Lubrificanti',
  'Parti elettriche', 'Parti motore', 'Plastiche', 'Pneumatici', 'Protezioni moto',
  'Ruote', 'Sospensioni', 'Trasmissione', 'Altro',
];

/**
 * ImportFromSupplierModal — three-step wizard for multi-variant supplier imports.
 *   1. Admin pastes a supplier product URL
 *   2. System scrapes the base product + all color variants
 *   3. Admin reviews shared fields (name, category), sets price/stock per variant,
 *      chooses images, and bulk-creates products
 */
const ImportFromSupplierModal = ({ onClose, onSaved }) => {
  const { language } = useLanguage();
  const t = (it, en, es) => (language === 'it' ? it : language === 'es' ? es : en);

  const [step, setStep] = useState(1); // 1 = URL, 2 = review, 3 = result
  const [url, setUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supported, setSupported] = useState([]);
  const [base, setBase] = useState(null);
  const [variants, setVariants] = useState([]); // enriched with UI state
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkStock, setBulkStock] = useState('');
  const [result, setResult] = useState(null);
  const [duplicates, setDuplicates] = useState({}); // { sku: { slug, name_it } }

  // Re-check duplicate SKUs whenever variants' SKU list changes
  useEffect(() => {
    const skus = variants.map((v) => v.sku).filter(Boolean);
    if (skus.length === 0) { setDuplicates({}); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/admin/check-skus`,
          { skus },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const map = {};
        for (const p of data?.existing || []) map[p.sku] = p;
        setDuplicates(map);
        // Auto-uncheck duplicates on the first check after scraping
        setVariants((vs) => vs.map((v) => (map[v.sku] ? { ...v, include: false } : v)));
      } catch (_) {
        setDuplicates({});
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [variants.map((v) => v.sku).join('|')]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/supported-suppliers`)
      .then(({ data }) => setSupported(data.suppliers || []))
      .catch(() => setSupported([]));
  }, []);

  const handleScrape = async () => {
    const cleanUrl = url.trim();
    if (!cleanUrl) {
      toast.error(t('Inserisci un URL fornitore', 'Enter a supplier URL', 'Introduce una URL del proveedor'));
      return;
    }
    setScraping(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/scrape-supplier`,
        { url: cleanUrl },
        { headers: { 'Content-Type': 'application/json' }, timeout: 120000 }
      );
      setBase({
        supplier: data.supplier,
        supplier_name: data.supplier_name,
        base_code: data.base_code,
        name_it: data.name_it || '',
        name_en: data.name_en || '',
        description_it: data.description_it || '',
        description_en: data.description_en || '',
        brand: data.brand || '',
        category: data.category || 'Altro',
        supplier_category: data.supplier_category || '',
        materials: data.materials || [],
        made_in: data.made_in || '',
        source_url: data.source_url,
      });
      setVariants(
        (data.variants || []).map((v) => ({
          include: true,
          sku: v.suggested_sku || '',
          color: v.color || '',
          ean: v.ean || '',
          name_it: v.name_it || '',
          name_en: v.name_en || v.name_it || '',
          price: '',
          stock: '',
          weight: String(v.weight ?? 0.5),
          images: v.images || [],
          selectedImages: (v.images || []).reduce((acc, u) => ({ ...acc, [u]: true }), {}),
        }))
      );
      setStep(2);
      const n = data.variants?.length || 0;
      toast.success(
        n === 1
          ? t('Trovata 1 variante', 'Found 1 variant', 'Encontrada 1 variante')
          : t(`Trovate ${n} varianti`, `Found ${n} variants`, `Encontradas ${n} variantes`),
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('Errore scraping', 'Scraping error', 'Error scraping');
      toast.error(msg);
    } finally {
      setScraping(false);
    }
  };

  const applyBulkPrice = () => {
    const p = parseFloat(bulkPrice);
    if (isNaN(p) || p < 0) {
      toast.error(t('Prezzo non valido', 'Invalid price', 'Precio no válido'));
      return;
    }
    setVariants((vs) => vs.map((v) => ({ ...v, price: String(p) })));
    toast.success(t('Prezzo applicato a tutte le varianti', 'Price applied to all variants', 'Precio aplicado'));
  };

  const applyBulkStock = () => {
    const s = parseInt(bulkStock, 10);
    if (isNaN(s) || s < 0) {
      toast.error(t('Stock non valido', 'Invalid stock', 'Stock no válido'));
      return;
    }
    setVariants((vs) => vs.map((v) => ({ ...v, stock: String(s) })));
    toast.success(t('Stock applicato a tutte le varianti', 'Stock applied to all variants', 'Stock aplicado'));
  };

  const updateVariant = (idx, patch) => {
    setVariants((vs) => vs.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const toggleVariantImage = (idx, img) => {
    setVariants((vs) =>
      vs.map((v, i) =>
        i === idx
          ? { ...v, selectedImages: { ...v.selectedImages, [img]: !v.selectedImages[img] } }
          : v,
      ),
    );
  };

  const handleSave = async () => {
    const toCreate = variants.filter((v) => v.include);
    if (toCreate.length === 0) {
      toast.error(t('Seleziona almeno una variante', 'Select at least one variant', 'Selecciona una variante'));
      return;
    }
    // Validation
    for (const v of toCreate) {
      if (!v.sku.trim()) {
        toast.error(t(`SKU mancante per ${v.color}`, `Missing SKU for ${v.color}`, `SKU faltante para ${v.color}`));
        return;
      }
      const p = parseFloat(v.price);
      if (isNaN(p) || p < 0) {
        toast.error(t(`Prezzo mancante per SKU ${v.sku}`, `Missing price for SKU ${v.sku}`, `Precio faltante para SKU ${v.sku}`));
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        variants: toCreate.map((v) => ({
          sku: v.sku.trim(),
          name_it: v.name_it.trim(),
          name_en: (v.name_en || v.name_it).trim(),
          category: base.category,
          description_it: base.description_it,
          description_en: base.description_en,
          price: parseFloat(v.price),
          stock: parseInt(v.stock || '0', 10),
          images: v.images.filter((u) => v.selectedImages[u]),
          brand: base.brand || null,
          weight: parseFloat(v.weight || '0.5'),
          color: v.color || null,
          ean: v.ean || null,
          source_url: base.source_url,
          supplier: base.supplier,
        })),
      };
      const { data } = await axios.post(
        `${API_URL}/api/admin/create-supplier-products`,
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: 60000 },
      );
      setResult(data);
      setStep(3);
      if (data.created_count > 0) {
        toast.success(
          t(`${data.created_count} prodotto/i creato/i`, `${data.created_count} product(s) created`, `${data.created_count} producto(s) creado(s)`),
        );
      }
      if (data.skipped_count > 0) {
        toast.warning(
          t(`${data.skipped_count} scartato/i (SKU esistente)`, `${data.skipped_count} skipped (SKU exists)`, `${data.skipped_count} omitido(s)`),
        );
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('Errore salvataggio', 'Save error', 'Error al guardar');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const supportedDomains = supported.flatMap((s) => s.domains).join(', ');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto border"
        style={{ background: '#0f0f0f', borderColor: '#2a2a2a' }}
        data-testid="import-supplier-modal"
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between px-6 py-4 border-b z-10"
          style={{ background: '#0f0f0f', borderColor: '#2a2a2a' }}
        >
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Truck size={22} className="text-[#FF6600]" />
            {step === 1 && t('Importa da Fornitore', 'Import from Supplier', 'Importar de Proveedor')}
            {step === 2 && t(`Rivedi varianti (${variants.length})`, `Review variants (${variants.length})`, `Revisar variantes (${variants.length})`)}
            {step === 3 && t('Risultato import', 'Import result', 'Resultado')}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-supplier-modal">
            <X size={24} />
          </button>
        </div>

        {/* STEP 1 — URL */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <div className="bg-[#FF6600]/10 border-l-4 border-[#FF6600] p-4 text-sm text-white/90">
              <p className="mb-1 font-semibold">{t('Come funziona', 'How it works', 'Cómo funciona')}</p>
              <ol className="list-decimal ml-5 space-y-1 text-white/70">
                <li>{t('Incolla il link di un prodotto fornitore', 'Paste a supplier product link', 'Pega el enlace de producto')}</li>
                <li>{t('Il sistema estrae automaticamente tutte le varianti (colori, taglie...)', 'System auto-extracts all variants (colors, sizes...)', 'El sistema extrae todas las variantes')}</li>
                <li>{t('Imposta prezzo e stock per ogni variante e salva', 'Set price and stock for each variant and save', 'Establece precio y stock y guarda')}</li>
              </ol>
              <p className="mt-3 text-xs text-white/60">
                {t('Fornitori supportati:', 'Supported suppliers:', 'Proveedores soportados:')}{' '}
                <span className="text-[#FF6600] font-mono" data-testid="supported-suppliers">{supportedDomains || 'marketing.acerbis.it, pirelli.com'}</span>
              </p>
              <p className="mt-1 text-xs text-white/60">
                {t(
                  'Per gli altri siti pubblici l\'AI legge la pagina (può richiedere fino a 1 minuto): controlla sempre i dati prima di salvare. I portali con login non sono supportati.',
                  'For other public sites the AI reads the page (up to 1 minute): always check the data before saving. Login-only portals are not supported.',
                  'Para otros sitios públicos la IA lee la página (hasta 1 minuto): revisa los datos antes de guardar. Los portales con login no son compatibles.',
                )}
              </p>
            </div>

            <label className="block text-sm font-semibold text-white">
              {t('URL prodotto fornitore', 'Supplier product URL', 'URL del producto')}
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.pirelli.com/tyres/it-it/moto/... o qualsiasi link prodotto"
              className="w-full px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
              style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
              data-testid="supplier-url-input"
              onKeyDown={(e) => e.key === 'Enter' && handleScrape()}
            />

            <button
              onClick={handleScrape}
              disabled={scraping || !url.trim()}
              className="w-full flex items-center justify-center gap-2 bg-[#FF6600] text-black font-bold py-3 px-6 hover:bg-[#ff8833] transition-colors disabled:opacity-50"
              data-testid="scrape-supplier-btn"
            >
              {scraping ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
              {scraping
                ? t('Estrazione in corso...', 'Extracting...', 'Extrayendo...')
                : t('Estrai varianti dal link', 'Extract variants from link', 'Extraer variantes')}
            </button>

            <div className="text-xs text-white/50 flex items-start gap-2 mt-4">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-yellow-500" />
              <span>
                {t(
                  'I cataloghi fornitori spesso non mostrano prezzi/stock: dovrai inserirli manualmente prima del salvataggio.',
                  'Supplier catalogs often lack price/stock: you must set them manually before saving.',
                  'Los catálogos de proveedores no muestran precio/stock: debes ponerlos manualmente.',
                )}
              </span>
            </div>
          </div>
        )}

        {/* STEP 2 — Review */}
        {step === 2 && base && (
          <div className="p-6 space-y-6">
            <button
              onClick={() => setStep(1)}
              className="text-sm text-white/60 hover:text-white underline"
              data-testid="back-to-supplier-step1"
            >
              ← {t('Cambia URL', 'Change URL', 'Cambiar URL')}
            </button>

            {/* Base info card */}
            <div
              className="p-4 border space-y-3"
              style={{ background: '#151515', borderColor: '#2a2a2a' }}
            >
              <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-widest">
                <Package size={14} className="text-[#FF6600]" />
                {base.supplier_name} · {t('Codice base', 'Base code', 'Código base')}: {base.base_code}
                {base.supplier_category && ` · ${base.supplier_category}`}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label={t('Nome IT', 'Name IT', 'Nombre IT')}
                  value={base.name_it}
                  onChange={(v) => setBase({ ...base, name_it: v })}
                  testid="base-name-it"
                />
                <Field
                  label={t('Marca', 'Brand', 'Marca')}
                  value={base.brand}
                  onChange={(v) => setBase({ ...base, brand: v })}
                  testid="base-brand"
                />
              </div>

              <Field
                label={t('Descrizione IT', 'Description IT', 'Descripción IT')}
                value={base.description_it}
                onChange={(v) => setBase({ ...base, description_it: v })}
                textarea
                rows={4}
                testid="base-desc-it"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    {t('Categoria ALLMX', 'ALLMX Category', 'Categoría ALLMX')}
                  </label>
                  <select
                    value={base.category}
                    onChange={(e) => setBase({ ...base, category: e.target.value })}
                    className="w-full px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
                    style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
                    data-testid="base-category"
                  >
                    {ALLMX_CATEGORIES.map((c) => (
                      <option key={c} value={c} style={{ background: '#1a1a1a' }}>{c}</option>
                    ))}
                  </select>
                </div>
                {base.materials?.length > 0 && (
                  <div className="text-xs text-white/60">
                    <div className="font-semibold text-white/80 mb-1">
                      {t('Materiali (auto)', 'Materials (auto)', 'Materiales (auto)')}
                      {base.made_in && ` · Made in ${base.made_in}`}
                    </div>
                    <div className="max-h-20 overflow-y-auto">
                      {base.materials.map((m, i) => (
                        <span key={i} className="mr-2">{m};</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Duplicate SKUs global warning */}
            {Object.keys(duplicates).length > 0 && (
              <div
                className="p-4 border-l-4 border-red-500 bg-red-500/10 text-white/90 flex items-start gap-3"
                data-testid="supplier-duplicates-warning"
              >
                <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-bold text-red-400 mb-1">
                    {t(
                      `${Object.keys(duplicates).length} di ${variants.length} SKU già presenti sul sito`,
                      `${Object.keys(duplicates).length} of ${variants.length} SKUs already in catalog`,
                      `${Object.keys(duplicates).length} de ${variants.length} SKUs ya en el catálogo`,
                    )}
                  </div>
                  <div className="text-white/70">
                    {t(
                      'Le varianti duplicate sono state deselezionate automaticamente. Cambia lo SKU se vuoi crearle come nuovi prodotti.',
                      'Duplicate variants have been auto-unchecked. Change the SKU to create them as new products.',
                      'Las variantes duplicadas han sido desmarcadas automáticamente.',
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bulk price/stock */}
            <div
              className="p-4 border flex flex-wrap items-end gap-3"
              style={{ background: '#151515', borderColor: '#2a2a2a' }}
            >
              <div className="flex items-center gap-2 text-xs text-[#FF6600] font-bold uppercase tracking-widest">
                <Copy size={14} />
                {t('Applica a tutte le varianti', 'Apply to all variants', 'Aplicar a todas')}
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs text-white/70 mb-1">
                    {t('Prezzo € per tutte', 'Bulk price €', 'Precio € para todas')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={bulkPrice}
                    onChange={(e) => setBulkPrice(e.target.value)}
                    className="w-28 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
                    style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
                    data-testid="bulk-price-input"
                  />
                </div>
                <button
                  onClick={applyBulkPrice}
                  className="px-4 py-2 bg-[#FF6600] text-black font-bold hover:bg-[#ff8833]"
                  data-testid="apply-bulk-price"
                >
                  {t('Applica prezzo', 'Apply price', 'Aplicar')}
                </button>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs text-white/70 mb-1">
                    {t('Stock per tutte', 'Bulk stock', 'Stock para todas')}
                  </label>
                  <input
                    type="number"
                    value={bulkStock}
                    onChange={(e) => setBulkStock(e.target.value)}
                    className="w-24 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
                    style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
                    data-testid="bulk-stock-input"
                  />
                </div>
                <button
                  onClick={applyBulkStock}
                  className="px-4 py-2 bg-[#FF6600] text-black font-bold hover:bg-[#ff8833]"
                  data-testid="apply-bulk-stock"
                >
                  {t('Applica stock', 'Apply stock', 'Aplicar')}
                </button>
              </div>
            </div>

            {/* Variants */}
            <div className="space-y-4">
              {variants.map((v, idx) => {
                const dup = duplicates[v.sku];
                return (
                <div
                  key={v.sku + idx}
                  className="border p-4 space-y-3"
                  style={{
                    background: v.include ? '#141414' : '#0a0a0a',
                    borderColor: dup ? '#dc2626' : (v.include ? '#FF6600' : '#2a2a2a'),
                    opacity: v.include ? 1 : 0.5,
                  }}
                  data-testid={`variant-card-${idx}`}
                >
                  {dup && (
                    <div
                      className="p-2 bg-red-500/10 border border-red-500/40 text-xs text-red-300 flex items-center gap-2"
                      data-testid={`variant-dup-warning-${idx}`}
                    >
                      <AlertTriangle size={14} className="flex-shrink-0" />
                      <span className="flex-1">
                        {t('SKU già presente:', 'SKU already exists:', 'SKU ya existe:')}{' '}
                        <span className="text-white font-semibold">{dup.name_it}</span>
                      </span>
                      <a
                        href={`/products/${dup.slug || dup.sku}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#FF6600] hover:underline whitespace-nowrap"
                      >
                        <ExternalLink size={12} /> {t('Vedi', 'View', 'Ver')}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={v.include}
                        onChange={(e) => updateVariant(idx, { include: e.target.checked })}
                        className="w-5 h-5 accent-[#FF6600]"
                        data-testid={`variant-include-${idx}`}
                      />
                      <span className="font-bold text-white">
                        {v.color || v.sku}
                      </span>
                      <span className="text-xs text-white/60">
                        EAN: <span className="font-mono">{v.ean || '—'}</span>
                      </span>
                    </label>
                    <span className="text-xs text-white/40">
                      {v.images.filter((u) => v.selectedImages[u]).length}/{v.images.length}{' '}
                      {t('immagini', 'images', 'imágenes')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Field
                      label="SKU *"
                      value={v.sku}
                      onChange={(vv) => updateVariant(idx, { sku: vv })}
                      testid={`variant-sku-${idx}`}
                    />
                    <Field
                      label={t('Prezzo € *', 'Price € *', 'Precio € *')}
                      value={v.price}
                      onChange={(vv) => updateVariant(idx, { price: vv })}
                      type="number"
                      testid={`variant-price-${idx}`}
                    />
                    <Field
                      label="Stock"
                      value={v.stock}
                      onChange={(vv) => updateVariant(idx, { stock: vv })}
                      type="number"
                      testid={`variant-stock-${idx}`}
                    />
                    <Field
                      label={t('Peso (kg)', 'Weight (kg)', 'Peso (kg)')}
                      value={v.weight}
                      onChange={(vv) => updateVariant(idx, { weight: vv })}
                      type="number"
                      testid={`variant-weight-${idx}`}
                    />
                  </div>

                  <Field
                    label={t('Nome IT (per questa variante)', 'Name IT (this variant)', 'Nombre IT')}
                    value={v.name_it}
                    onChange={(vv) => updateVariant(idx, { name_it: vv })}
                    testid={`variant-name-${idx}`}
                  />

                  {/* Images gallery */}
                  {v.images.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-white/80 mb-2">
                        {t('Immagini (clicca per selezionare)', 'Images (click to select)', 'Imágenes (haz clic)')}
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {v.images.map((img) => (
                          <button
                            key={img}
                            type="button"
                            onClick={() => toggleVariantImage(idx, img)}
                            className="relative aspect-square border-2 overflow-hidden"
                            style={{
                              borderColor: v.selectedImages[img] ? '#FF6600' : '#2a2a2a',
                              background: '#1a1a1a',
                            }}
                            data-testid={`variant-toggle-image-${idx}`}
                          >
                            <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                            {v.selectedImages[img] && (
                              <div className="absolute top-1 right-1 bg-[#FF6600] rounded-full p-0.5">
                                <Check size={12} color="black" strokeWidth={3} />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Save */}
            <div className="sticky bottom-0 -mx-6 px-6 py-4 border-t"
              style={{ background: '#0f0f0f', borderColor: '#2a2a2a' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-[#FF6600] text-black font-bold py-3 px-6 hover:bg-[#ff8833] transition-colors disabled:opacity-50"
                data-testid="save-supplier-products-btn"
              >
                {saving ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
                {saving
                  ? t('Salvataggio...', 'Saving...', 'Guardando...')
                  : t(
                      `Crea ${variants.filter((v) => v.include).length} prodotto/i`,
                      `Create ${variants.filter((v) => v.include).length} product(s)`,
                      `Crear ${variants.filter((v) => v.include).length} producto(s)`,
                    )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Result */}
        {step === 3 && result && (
          <div className="p-6 space-y-5">
            {result.created_count > 0 && (
              <div className="p-4 border-l-4 border-green-500 bg-green-500/10 text-white/90">
                <div className="font-bold text-green-400 mb-2">
                  ✓ {result.created_count} {t('prodotto/i creato/i', 'product(s) created', 'producto(s) creado(s)')}
                </div>
                <ul className="text-sm space-y-1">
                  {result.created.map((c) => (
                    <li key={c.sku} className="text-white/70 font-mono">
                      {c.sku} — {c.name_it}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.skipped_count > 0 && (
              <div className="p-4 border-l-4 border-yellow-500 bg-yellow-500/10 text-white/90">
                <div className="font-bold text-yellow-400 mb-2">
                  ⚠ {result.skipped_count} {t('scartato/i (SKU già esistente)', 'skipped (SKU already exists)', 'omitido(s)')}
                </div>
                <ul className="text-sm space-y-1">
                  {result.skipped.map((s) => (
                    <li key={s.sku} className="text-white/70 font-mono">
                      {s.sku} — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.error_count > 0 && (
              <div className="p-4 border-l-4 border-red-500 bg-red-500/10 text-white/90">
                <div className="font-bold text-red-400 mb-2">
                  ✕ {result.error_count} {t('errori', 'errors', 'errores')}
                </div>
                <ul className="text-sm space-y-1">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-white/70 font-mono">
                      {e.sku}: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep(1);
                  setUrl('');
                  setBase(null);
                  setVariants([]);
                  setResult(null);
                }}
                className="flex-1 px-4 py-3 border border-[#FF6600] text-[#FF6600] font-bold hover:bg-[#FF6600] hover:text-black"
                data-testid="import-another-btn"
              >
                {t('Importa un altro prodotto', 'Import another product', 'Importar otro')}
              </button>
              <button
                onClick={() => {
                  onSaved && onSaved(result);
                  onClose();
                }}
                className="flex-1 bg-[#FF6600] text-black font-bold px-4 py-3 hover:bg-[#ff8833]"
                data-testid="close-after-import-btn"
              >
                {t('Chiudi e ricarica', 'Close and reload', 'Cerrar y recargar')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', textarea = false, rows = 3, testid }) => (
  <div>
    <label className="block text-xs font-semibold text-white/80 mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6600] resize-y"
        style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
        style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
        data-testid={testid}
      />
    )}
  </div>
);

export default ImportFromSupplierModal;
